import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import styled from 'styled-components';
import { commifyNumber, getPhoneNumber } from '../../../../../helpers/common';
import {
  handleCardClickEvent,
  handleCardContactEvent
} from '../../../../../helpers/cards';
import { CTATypes, EntityTypes } from '../../../../../new-lib/constants/common';
import WhatsappBuilder from '../../../../../helpers/whatsapp/whatsapp-builder';
import { phoneHelper } from '../../../../../helpers/phone-helper';
import { trackEvent } from '../../../../../new-lib/remarketing';
import ButtonIcon from '../../../../../new-components/ui/ButtonIcon';
import CallIcon from '../../../../../new-components/svg-icons/CallIcon';
import WhatsappIcon from '../../../../../new-components/svg-icons/WhatsappIcon';

interface CardProps {
  launch: any;
  position: number;
}
const CompoundLaunchCard = ({ launch, position }: CardProps) => {
  const { t } = useTranslation('launches');
  const { asPath } = useRouter();
  const referer = useSelector((state: any) => state.app.referer);
  const userId = useSelector((state: any) => state.app.userId);
  const eventId = useSelector((state: any) => state.app.eventId);
  const whatsapp = new WhatsappBuilder(
    launch,
    asPath,
    referer,
    userId,
    eventId,
    CTATypes.LAUNCH
  );
  const PHONE_NUMBER = getPhoneNumber(referer);

  const clickEventData: any = {
    entity: launch,
    eventAction: 'lnch_click',
    recommended: false,
    cardPosition: position + 1,
    featureName: 'launch'
  };

  const handleWhatsAppClick = (event: MouseEvent): void => {
    event.preventDefault();
    whatsapp.launchWhatsApp();
    handleCardContactEvent({
      ...clickEventData,
      eventAction: 'whatsapp',
      eventId: eventId,
      entityType: EntityTypes.LAUNCH
    });
    trackEvent.purchase({
      listingIds: [launch.id],
      listingPrice: launch.min_price
    });
  };
  const handlePhoneCall = (e: any) => {
    e.stopPropagation();
    phoneHelper(launch, asPath, Number(PHONE_NUMBER), CTATypes.LAUNCH);
    handleCardContactEvent({
      ...clickEventData,
      eventAction: 'call',
      eventId: eventId,
      entityType: EntityTypes.LAUNCH
    });
    trackEvent.purchase({
      listingIds: [launch.id],
      listingPrice: launch.min_price
    });
  };
  return (
    <Link href={`/compound/${launch.slug}`} prefetch legacyBehavior>
      <Container onClick={() => handleCardClickEvent(clickEventData)}>
        <div className="right-container">
          <h3 className="new-launch">{t('launching_soon')}</h3>

          <h4 className="launch-title">{launch.short_title}</h4>

          <div className="price-cta">
            {launch.starting_price ? (
              <div className="price-container">
                <span>{t('start_from')}</span>
                <h3 className="price">
                  {`${commifyNumber(launch.starting_price)} ${
                    launch.currency || 'EGP'
                  }`}
                </h3>
              </div>
            ) : (
              <br />
            )}
            <div>
              <a
                href={`tel:+2${PHONE_NUMBER}`}
                onClick={e => {
                  handlePhoneCall(e);
                }}
              >
                <ButtonIcon
                  backgroundColor="#E4EBF2"
                  color=""
                  border="none"
                  icon={<CallIcon />}
                  onClick={() => {}}
                />
              </a>
              <ButtonIcon
                backgroundColor="#4CD964"
                color=""
                border="none"
                icon={<WhatsappIcon />}
                onClick={(event: any) => handleWhatsAppClick(event)}
              />
            </div>
          </div>
        </div>
      </Container>
    </Link>
  );
};

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 125px;
  display: flex;
  background: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.2) 0%,
      rgba(0, 0, 0, 0.2) 100%
    ),
    linear-gradient(90deg, #1e4164 0%, rgba(30, 65, 100, 0.63) 100%);

  border: 1px solid #e6e6e6;
  border-radius: ${props => props.theme.borderRadius};

  .right-container {
    width: 100%;
    height: 100%;
    padding: 8px;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;

    h3 {
      font-size: ${props => props.theme.font.sizes.l};
      color: ${props => props.theme.light.colors.white};
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;

      &.new-launch {
        font-family: materialMedium;
        margin-bottom: 4px;
      }

      &.price {
        font-family: materialBold;
      }
    }

    h4 {
      font-family: materialBold;
      font-size: ${props => props.theme.font.sizes.xl};
      color: #ccae80;
      margin-bottom: 4px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .price-cta {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .price-container {
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        span {
          font-size: 10px;
          color: ${props => props.theme.light.colors.white};
        }
      }

      > div {
        display: flex;
        align-items: center;
        gap: 5px;
      }
    }
  }
`;

export default CompoundLaunchCard;
